/**
 * One way to create the external (EAPI) trips for an order being confirmed.
 *
 * Under the hood an order is "one or more legs". A one-leg order is simply the
 * n = 1 case: it goes through the exact same fan-out, rollback and reporting as
 * a five-leg order. Callers build the payloads (they own the timezone/asset
 * resolution); this module owns the invariant part — send them all at once,
 * and if any one fails, cancel the ones that succeeded so we never leave
 * orphaned trips upstream while the local order stays unconfirmed.
 */

export interface PromoteTripRequest {
  /** 1-based leg position. A single-leg order passes 1. */
  legSequence: number;
  /** Payload for `createTrip`, already built by the caller. */
  tripBody: unknown;
  /** Label used in logs and in the assertion context. */
  label: string;
  /** Passed through to the caller-supplied `create` implementation. */
  assert: unknown;
}

export interface CreatedLegTrip {
  legSequence: number;
  eapiTripId: string;
}

export interface CreateTripsForLegsParams {
  requests: PromoteTripRequest[];
  /**
   * Issues one create call. Kept injectable so this module stays free of the
   * EAPI client and remains unit-testable.
   */
  create: (req: PromoteTripRequest) => Promise<string>;
  /** Cancels a trip during rollback. Failures here are logged, never thrown. */
  cancel: (eapiTripId: string, reason: string) => Promise<unknown>;
  /** Reason string sent with rollback cancels. */
  rollbackReason: string;
  /** Prefix for console diagnostics, e.g. "multi-leg promote". */
  logLabel: string;
}

/**
 * Creates every requested trip in parallel.
 *
 * Resolves with the created trips (ordered by leg sequence) when all succeed.
 * On any failure, cancels the trips that did succeed and rejects with a
 * message naming the first failing leg.
 */
export async function createTripsForLegs({
  requests,
  create,
  cancel,
  rollbackReason,
  logLabel,
}: CreateTripsForLegsParams): Promise<CreatedLegTrip[]> {
  if (requests.length === 0) return [];

  const settled = await Promise.allSettled(requests.map((req) => create(req)));

  const created: CreatedLegTrip[] = [];
  const failures: Array<{ legSequence: number; reason: unknown }> = [];

  settled.forEach((result, i) => {
    const legSequence = requests[i].legSequence;
    if (result.status === "fulfilled" && result.value) {
      created.push({ legSequence, eapiTripId: result.value });
    } else {
      failures.push({
        legSequence,
        reason: result.status === "rejected" ? result.reason : new Error("EAPI response did not include a trip ID"),
      });
    }
  });

  if (failures.length === 0) {
    created.sort((a, b) => a.legSequence - b.legSequence);
    return created;
  }

  // Roll back everything that did get created so the upstream system doesn't
  // keep trips for an order that stays unconfirmed locally.
  for (const c of created) {
    try {
      await cancel(c.eapiTripId, rollbackReason);
      console.log(`[${logLabel}] rolled back leg ${c.legSequence} trip ${c.eapiTripId}`);
    } catch (rollbackErr: any) {
      console.warn(
        `[${logLabel}] rollback cancel failed for leg ${c.legSequence}:`,
        rollbackErr?.message || rollbackErr,
      );
    }
  }

  failures.sort((a, b) => a.legSequence - b.legSequence);
  const first = failures[0];
  const message = (first.reason as any)?.message || String(first.reason);
  throw new Error(`Failed to create external trip for leg ${first.legSequence}: ${message}`);
}
